import { useTranslation } from 'react-i18next';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { patch, post } from '../../nucleo/api';
import { textoDoVinculo, useOpcoesModuloAlvo, useRotuloColuna, type VinculoNovo } from './comum';
import { PRIORIDADES, TAMANHOS, TIPOS, type Card, type Quadro } from './tipos';

/** Corpo enviado à API: texto vazio vira nulo (como a API espera); etiquetas sem vazio. */
function corpo(v: Record<string, unknown>) {
  const txt = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  return {
    titulo: v.titulo,
    descricao: typeof v.descricao === 'string' && v.descricao.trim() ? v.descricao : null,
    tipo: txt(v.tipo),
    prioridade: txt(v.prioridade) ?? 'P3',
    tamanho: txt(v.tamanho),
    modulo_alvo: txt(v.modulo_alvo),
    responsavel_id: txt(v.responsavel_id),
    prazo: txt(v.prazo),
    etiquetas: Array.isArray(v.etiquetas) ? (v.etiquetas as string[]).map((e) => e.trim()).filter(Boolean) : [],
    solicitante: txt(v.solicitante),
  };
}

/** Formulário de criação e edição do card. O botão de enviar fica no rodapé do Drawer (`form="form-card"`). */
export function FormCard({
  atual,
  quadro,
  usuarioId,
  somenteLeitura,
  vinculo,
  resumido,
  aoSalvar,
}: {
  atual?: Card;
  quadro?: Quadro;
  usuarioId?: string;
  somenteLeitura?: boolean;
  /** Fase 2 (W23, K11): card novo a partir de um registro ("Criar tarefa" no chatter): nasce ligado a ele. */
  vinculo?: VinculoNovo;
  /** U15 F8 (atalho rápido): só título, responsável e prazo; o resto sai com os padrões do módulo (prioridade P3, primeira coluna). */
  resumido?: boolean;
  aoSalvar: (r: { id?: string }) => Promise<void> | void;
}) {
  const { t } = useTranslation('tarefas');
  const alvos = useOpcoesModuloAlvo();
  const rotuloColuna = useRotuloColuna();
  const d = somenteLeitura;
  const todos: DefCampo[] = [
    { nome: 'titulo', rotulo: t('campos.titulo'), tipo: 'texto', obrigatorio: true, desabilitado: d },
    ...(!atual && quadro
      ? [
          {
            nome: 'coluna_id',
            rotulo: t('campos.coluna'),
            tipo: 'selecao' as const,
            largura: 'meia' as const,
            obrigatorio: true,
            opcoes: quadro.colunas.map((c) => ({ valor: c.id, rotulo: rotuloColuna(c) })),
          },
        ]
      : []),
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', largura: 'meia', desabilitado: d, opcoes: TIPOS.map((x) => ({ valor: x, rotulo: `${x} · ${t(`tipos.${x}`)}` })) },
    { nome: 'prioridade', rotulo: t('campos.prioridade'), tipo: 'selecao', largura: 'meia', obrigatorio: true, desabilitado: d, opcoes: PRIORIDADES.map((x) => ({ valor: x, rotulo: t(`prioridades.${x}`) })) },
    { nome: 'tamanho', rotulo: t('campos.tamanho'), tipo: 'selecao', largura: 'meia', desabilitado: d, opcoes: TAMANHOS.map((x) => ({ valor: x, rotulo: t(`tamanhos.${x}`) })) },
    { nome: 'modulo_alvo', rotulo: t('campos.moduloAlvo'), tipo: 'selecao', largura: 'meia', desabilitado: d, opcoes: alvos },
    { nome: 'responsavel_id', rotulo: t('campos.responsavel'), tipo: 'busca', largura: 'meia', desabilitado: d, busca: { caminho: '/membros', rotulo: (i) => String(i.nome) } },
    { nome: 'prazo', rotulo: t('campos.prazo'), tipo: 'data', largura: 'meia', desabilitado: d },
    { nome: 'solicitante', rotulo: t('campos.solicitante'), tipo: 'texto', largura: 'meia', desabilitado: d },
    { nome: 'etiquetas', rotulo: t('campos.etiquetas'), tipo: 'tags', ajuda: t('campos.etiquetasAjuda'), desabilitado: d },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area', desabilitado: d },
  ];
  const campos = resumido && !atual ? todos.filter((c) => ['titulo', 'responsavel_id', 'prazo'].includes(c.nome)) : todos;
  const inicial: Record<string, unknown> = atual
    ? { ...atual }
    : { prioridade: 'P3', coluna_id: quadro?.colunas[0]?.id ?? null, responsavel_id: usuarioId ?? null, etiquetas: [] };
  return (
    <>
    {vinculo && !atual && <p className="mb-4 text-sm text-tinta-2" data-vinculo-novo>{t('registro.vinculadoA', { registro: textoDoVinculo(vinculo.entidade, vinculo.titulo) })}</p>}
    <Formulario
      key={atual?.id ?? 'novo'}
      id="form-card"
      campos={campos}
      inicial={inicial}
      rotulosAtuais={atual ? { responsavel_id: atual.responsavel_nome ?? '' } : undefined}
      aoEnviar={async (v) => {
        if (somenteLeitura) return;
        if (atual) {
          await patch(`/tarefas/cards/${atual.id}`, corpo(v));
          await aoSalvar({});
        } else {
          const r = await post<{ id: string }>('/tarefas/cards', { ...corpo(v), quadro_id: quadro?.id, coluna_id: v.coluna_id || undefined, ...(vinculo ? { vinculo } : {}) });
          await aoSalvar(r);
        }
      }}
    />
    </>
  );
}
